/**
 * Maps vehicle make strings from the API to brand icon paths in public/icon/car/.
 */

/**
 * Aliases for brands where the API name does NOT convert cleanly
 * via normalization (lowercase + replace spaces/hyphens with underscores).
 */
const BRAND_ALIASES: Record<string, string> = {
  volkswagen: 'vw',
  'mercedes-benz': 'mercedes',
  'mercedes benz': 'mercedes',
  mini: 'mini_cooper',
  peugeot: 'peugot', // matches the misspelled filename
};

/** All available brand icon stems (without "brand_" prefix and extension). */
const AVAILABLE_BRANDS = new Set([
  'acura',
  'alfa_romeo',
  'aro',
  'aston_martin',
  'audi',
  'avia',
  'bentley',
  'bmw',
  'bristol',
  'bugatti',
  'buick',
  'cadillac',
  'chatenet',
  'chevrolet',
  'chrysler',
  'citroen',
  'cupra',
  'dacia',
  'daewoo',
  'daewoo_avia',
  'daf',
  'daihatsu',
  'datsun',
  'dodge',
  'ferrari',
  'fiat',
  'ford',
  'gordon',
  'great_wall',
  'gumpert',
  'holden',
  'honda',
  'hummer',
  'hyundai',
  'infiniti',
  'invicta',
  'isuzu',
  'iveco',
  'jaguar',
  'jeep',
  'kaipan',
  'kia',
  'knaus',
  'koenigsegg',
  'lada',
  'lamborghini',
  'lancia',
  'land_rover',
  'lexus',
  'liaz',
  'lincoln',
  'magma',
  'mahindra',
  'man',
  'maserati',
  'matra',
  'mazda',
  'mclaren',
  'mercedes',
  'mg',
  'mini_cooper',
  'mitsubishi',
  'moskvic',
  'multicar',
  'nissan',
  'oltcit',
  'opel',
  'peugot',
  'pgo',
  'phanomen',
  'pontiac',
  'porsche',
  'praga',
  'renault',
  'rolls_royce',
  'rover',
  'saab',
  'saleen',
  'seat',
  'simca',
  'skoda',
  'smart',
  'ssangyong',
  'subaru',
  'suzuki',
  'tatra',
  'tesla',
  'toyota',
  'trabant',
  'venturi',
  'volvo',
  'vw',
  'wartburg',
  'wiesmann',
]);

/** File extension override for brands that are not SVG */
const EXTENSION_OVERRIDES: Record<string, string> = {
  pgo: 'png',
};

const ICON_BASE_PATH = '/icon/car';
const FALLBACK_ICON = `${ICON_BASE_PATH}/brand_other.svg`;

function normalizeMake(make: string): string {
  return make
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, '_');
}

/**
 * Returns the icon path for a given vehicle make string.
 * Case-insensitive with alias support. Falls back to brand_other.svg.
 */
export function getVehicleBrandIconPath(make: string | undefined | null): string {
  if (!make) return FALLBACK_ICON;

  const lowerMake = make.toLowerCase().trim();

  // Check explicit aliases first
  const aliasKey = BRAND_ALIASES[lowerMake];
  if (aliasKey && AVAILABLE_BRANDS.has(aliasKey)) {
    const ext = EXTENSION_OVERRIDES[aliasKey] || 'svg';
    return `${ICON_BASE_PATH}/brand_${aliasKey}.${ext}`;
  }

  // Try normalized name
  const normalized = normalizeMake(make);
  if (AVAILABLE_BRANDS.has(normalized)) {
    const ext = EXTENSION_OVERRIDES[normalized] || 'svg';
    return `${ICON_BASE_PATH}/brand_${normalized}.${ext}`;
  }

  return FALLBACK_ICON;
}
